import _ from 'lodash';
import { Reducer } from 'redux';
import { IApplicationState } from '.';
import { IExternalAttributeVm, ISurveyQuestionEditModel } from '../models';
import * as ReportingAttributeAPI from '../services/AttributeAPI';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';

export interface IClientParticipantAttributeState {
	loading: boolean;
	participantAttributes: IExternalAttributeVm[];
	selectedAttributes: string[];
}

export const defaultState: IClientParticipantAttributeState = {
	loading: false,
	participantAttributes: [],
	selectedAttributes: [],
};

export const ActionCreators = {
	GetConfiguredParticipantAttributes: (surveyId: number) => (dispatch: any, getState: () => IApplicationState) => {
		const { participantAttributes } = getState().clientParticipantAttributes;

		if (!participantAttributes.length) {
			dispatch({ type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_GET_REQUESTED });

			return ReportingAttributeAPI.getConfiguredParticipantAttributes(surveyId)
				.then((payload) => dispatch({ type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_GET_SUCCEEDED, payload }))
				.catch(() => dispatch({ type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_GET_FAILED }));
		} else {
			dispatch({ type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_GET_SUCCEEDED, payload: participantAttributes });
		}
	},
	ReplaceSelectedAttribute: (original?: string, next?: string) => ({
		type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REPLACE_SELECTED,
		payload: { original, next },
	}),
};

type KnownActions =
	| Actions.IClientParticipantAttributeGetRequested
	| Actions.IClientParticipantAttributeGetSucceeded
	| Actions.IClientParticipantAttributeGetFailed
	| Actions.IClientParticipantAttributesAddSelectedByColumn
	| Actions.IClientParticipantAttributesAddSelectedByName
	| Actions.IClientParticipantAttributesRemoveSelectedByColumn
	| Actions.IClientParticipantAttributesRemoveSelectedByName
	| Actions.IClientParticipantAttributesReplaceSelected
	| Actions.ISurveyGetSucceeded
	| Actions.IDeleteSurveyQuestion
	| Actions.ILibraryQuestionsSelected
	| Actions.ILibraryQuestionsVariationSelected
	| Actions.ICustomQuestionSelected
	| Actions.ICustomQuestionSaveSucceeded;

export const reducer: Reducer<IClientParticipantAttributeState, KnownActions> = (
	state: IClientParticipantAttributeState | undefined,
	action: KnownActions,
): IClientParticipantAttributeState => {
	if (!state) {
		return { ...defaultState };
	}

	let selectedAttributes = [...state.selectedAttributes] || [];
	switch (action.type) {
		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_GET_REQUESTED:
			return { ...state, loading: true };
		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_GET_FAILED:
			return { ...state, loading: false };
		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_GET_SUCCEEDED:
			return { ...state, loading: false, participantAttributes: action.payload };

		// SELECTED ATTRIBUTES
		case ActionTypes.SURVEY_GET_SUCCEEDED:
			selectedAttributes = action.payload.subject.questions
				.filter((q: ISurveyQuestionEditModel) => q.externalAttributeColumnName && q.isActive)
				.map((q: ISurveyQuestionEditModel) => q.externalAttributeColumnName as string);
			return { ...state, selectedAttributes };

		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_ADD_SELECTED_BYCOLUMN: {
			if (action.payload !== undefined && selectedAttributes.indexOf(action.payload) === -1) {
				selectedAttributes.push(action.payload);
				return { ...state, selectedAttributes };
			}
			break;
		}
		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_ADD_SELECTED_BYNAME: {
			const exAttr = state.participantAttributes.find((vm: IExternalAttributeVm) => vm.attributeName === action.payload);
			if (exAttr !== undefined && selectedAttributes.indexOf(exAttr.columnName) === -1) {
				selectedAttributes.push(exAttr.columnName);
				return { ...state, selectedAttributes };
			}
			break;
		}

		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYCOLUMN: {
			if (action.payload !== undefined) {
				selectedAttributes = selectedAttributes.filter((attr: string) => attr !== action.payload);
				return { ...state, selectedAttributes };
			}
			break;
		}
		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYNAME: {
			const exAttr = state.participantAttributes.find((vm: IExternalAttributeVm) => vm.attributeName === action.payload);
			if (exAttr !== undefined) {
				selectedAttributes = selectedAttributes.filter((attr: string) => attr !== exAttr.columnName);
				return { ...state, selectedAttributes };
			}
			break;
		}

		case ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REPLACE_SELECTED: {
			const { original, next } = action.payload;
			if (next !== undefined) {
				selectedAttributes = selectedAttributes.filter((attr: string) => attr !== original).concat([next]);
			}
			return { ...state, selectedAttributes };
		}
	}

	return { ...state };
};
